import React, { useEffect, useState } from "react";
import { Button, Dropdown, Form, InputGroup, Modal, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import api from "../../../api";
import Can from "../../../components/Can";
import ConfirmModal from "../../../components/common/ConfirmModal";
import { initialsOf, avatarColorFor } from "../../../utils/avatar";
import "./FormsBuildTab.css";

const initialForm = {
  name: "",
  code: "",
  description: "",
  instructions: "",
  form_type_id: "",
  department_id: "",
  project_id: "",
  project_module_id: "",
  visibility_scope: "ORGANIZATION",
  allow_multiple_submissions_default: false,
  requires_approval_default: false,
  contributes_to_performance_override: "",
};

const VISIBILITY_SCOPES = ["PRIVATE", "DEPARTMENT", "PROJECT", "ORGANIZATION"];

// Dynamic Forms visibility audit, §7: this tab is now also reached at
// /<role>/forms for every role, not just /admin/forms -- every full
// navigation below must follow the SAME role prefix rather than a
// hardcoded /admin/ path.
function useRolePrefix() {
  const path = window.location.pathname;
  const match = path.match(/^\/([^/]+)\//);
  return match ? match[1] : "admin";
}

// Build tab of the Forms Workspace -- combines what used to be three
// separate sidebar entries (Form Types, Forms, Form Builder) into one
// card-based "Build" surface. Form Types keeps its own dedicated CRUD page
// (/admin/form-types, unchanged) reached via "Manage Form Types" rather
// than being re-implemented inline here -- there's no current user flow
// that can't already be represented by that existing page, so duplicating
// its CRUD table inline would violate the "reuse, don't rebuild" brief for
// no real gain.
//
// "4-step wizard" mapping: Step 1 (Basic Details) is the modal below --
// the same fields the pre-consolidation "Add Form" modal collected. On
// save it hands off (via a full navigation, see the note on
// FormBuilderPage.jsx's `?tab=` fallback) to the EXISTING, already fully
// built drag-and-drop FormBuilderPage, whose own Structure / Scoring +
// Execution Profile / Preview tabs are Steps 2/3/4. Rebuilding that
// builder as a literal single-page wizard would mean re-implementing a
// substantial, working drag-and-drop engine from scratch for no behavior
// change -- this hands off to it instead, honoring "do not build duplicate
// versions of functioning components."
export default function FormsBuildTab() {
  const rolePrefix = useRolePrefix();
  const [forms, setForms] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [formTypes, setFormTypes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectModules, setProjectModules] = useState([]);

  const [search, setSearch] = useState("");
  const [formTypeFilter, setFormTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [projectFilter, setProjectFilter] = useState("");

  const [form, setForm] = useState(initialForm);
  const [editId, setEditId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyFormId, setBusyFormId] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteImpact, setDeleteImpact] = useState(null);
  const [deleteImpactLoading, setDeleteImpactLoading] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem("fbt-view-mode") || "grid"; } catch { return "grid"; }
  });
  const selectViewMode = (mode) => {
    setViewMode(mode);
    try { localStorage.setItem("fbt-view-mode", mode); } catch { /* best-effort only */ }
  };

  useEffect(() => {
    fetchForms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, formTypeFilter, statusFilter, projectFilter]);

  useEffect(() => {
    Promise.all([
      api.get("/form-types", { params: { is_active: 1, pageSize: 200 } }),
      api.get("/departments", { params: { is_active: 1, pageSize: 200 } }),
      api.get("/projects", { params: { is_active: 1, pageSize: 200 } }),
    ]).then(([typeRes, deptRes, projectRes]) => {
      setFormTypes(Array.isArray(typeRes.data?.data) ? typeRes.data.data : []);
      setDepartments(Array.isArray(deptRes.data?.data) ? deptRes.data.data : []);
      setProjects(Array.isArray(projectRes.data?.data) ? projectRes.data.data : []);
    }).catch((err) => console.error("Fetch form reference data error:", err));
  }, []);

  const fetchProjectModules = async (projectId) => {
    if (!projectId) { setProjectModules([]); return; }
    try {
      const res = await api.get(`/projects/${projectId}/modules`, { params: { is_active: 1, pageSize: 200 } });
      setProjectModules(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      console.error("Fetch project modules error:", err);
      setProjectModules([]);
    }
  };

  const fetchForms = async () => {
    try {
      setLoading(true);
      const res = await api.get("/forms", {
        params: {
          search: search || undefined,
          form_type_id: formTypeFilter || undefined,
          project_id: projectFilter || undefined,
          is_active: statusFilter === "" ? undefined : statusFilter,
          page: 1,
          pageSize: 200,
        },
      });
      setForms(Array.isArray(res.data?.data) ? res.data.data : []);
      setTotal(res.data?.meta?.total || 0);
    } catch (err) {
      console.error("Fetch forms error:", err);
      toast.error(err.response?.data?.message || "Failed to fetch forms");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const openAdd = () => {
    setForm(initialForm);
    setEditId(null);
    setProjectModules([]);
    setShowModal(true);
  };

  const openEdit = (formRow) => {
    setForm({
      name: formRow.name || "",
      code: formRow.code || "",
      description: formRow.description || "",
      instructions: formRow.instructions || "",
      form_type_id: formRow.form_type_id ?? "",
      department_id: formRow.department_id ?? "",
      project_id: formRow.project_id ?? "",
      project_module_id: formRow.project_module_id ?? "",
      visibility_scope: formRow.visibility_scope || "ORGANIZATION",
      allow_multiple_submissions_default: !!formRow.allow_multiple_submissions_default,
      requires_approval_default: !!formRow.requires_approval_default,
      contributes_to_performance_override: formRow.contributes_to_performance_override === null || formRow.contributes_to_performance_override === undefined
        ? ""
        : (formRow.contributes_to_performance_override ? "1" : "0"),
    });
    setEditId(formRow.id);
    if (formRow.project_id) fetchProjectModules(formRow.project_id);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditId(null);
    setForm(initialForm);
  };

  const goToBuilder = (formRow, tab) => {
    const versionId = formRow.draft_version_id || formRow.published_version_id;
    if (!versionId) {
      toast.warning("This form has no version to open yet.");
      return;
    }
    const suffix = tab ? `?tab=${tab}` : "";
    window.location.assign(`/${rolePrefix}/forms/${formRow.id}/builder/${versionId}${suffix}`);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.warning("Form name is required"); return; }
    if (!form.form_type_id) { toast.warning("Please select a form type"); return; }

    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      instructions: form.instructions.trim() || null,
      form_type_id: Number(form.form_type_id),
      department_id: form.department_id || null,
      project_id: form.project_id || null,
      project_module_id: form.project_module_id || null,
      visibility_scope: form.visibility_scope,
      allow_multiple_submissions_default: form.allow_multiple_submissions_default,
      requires_approval_default: form.requires_approval_default,
      contributes_to_performance_override: form.contributes_to_performance_override === "" ? null : form.contributes_to_performance_override === "1",
    };
    if (!editId) payload.code = form.code.trim() || undefined;

    try {
      setSaving(true);
      if (editId) {
        await api.put(`/forms/${editId}`, payload);
        toast.success("Form updated");
        closeModal();
        fetchForms();
      } else {
        const res = await api.post("/forms", payload);
        toast.success("Form created -- opening the builder");
        const created = res.data?.data || res.data;
        closeModal();
        if (created?.id && created?.draft_version_id) {
          window.location.assign(`/${rolePrefix}/forms/${created.id}/builder/${created.draft_version_id}`);
        } else {
          fetchForms();
        }
      }
    } catch (err) {
      console.error("Save form error:", err);
      const data = err.response?.data;
      if (data?.code === "FORM_PROJECT_MODULE_MISMATCH") {
        toast.error("The selected module does not belong to the selected project.");
      } else {
        toast.error(data?.message || "Failed to save form");
      }
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (formRow) => {
    try {
      await api.patch(`/forms/${formRow.id}/status`, { is_active: !formRow.is_active });
      toast.success(formRow.is_active ? "Form deactivated" : "Form activated");
      fetchForms();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update status");
    }
  };

  const openDeleteConfirm = async (row) => {
    setDeleteTarget(row);
    setDeleteImpact(null);
    setDeleteImpactLoading(true);
    try {
      const res = await api.get(`/forms/${row.id}/delete-impact`);
      setDeleteImpact(res.data?.data || null);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load delete impact");
    } finally {
      setDeleteImpactLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      const res = await api.delete(`/forms/${deleteTarget.id}`);
      toast.success(res.data?.data?.hardDeleted
        ? "Form deleted"
        : "Form archived -- historical submissions were preserved, and it no longer appears in the Forms list");
      setDeleteTarget(null);
      setDeleteImpact(null);
      fetchForms();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete form");
    } finally {
      setDeleteBusy(false);
    }
  };

  const publishDraft = async (formRow) => {
    if (!formRow.draft_version_id) return;
    setBusyFormId(formRow.id);
    try {
      await api.post(`/forms/${formRow.id}/versions/${formRow.draft_version_id}/publish`);
      toast.success("Form version published");
      fetchForms();
    } catch (err) {
      const data = err.response?.data;
      if (data?.code === "FORM_VERSION_INVALID") {
        toast.error(`Cannot publish: ${(data.details?.errors || []).length} validation issue(s). Opening the builder to review.`);
        goToBuilder(formRow, "preview");
      } else if (data?.code === "FORM_VERSION_ALREADY_PUBLISHED") {
        toast.error("Archive the current published version first.");
      } else {
        toast.error(data?.message || "Failed to publish form version");
      }
    } finally {
      setBusyFormId(null);
    }
  };

  const confirmArchivePublished = async () => {
    const formRow = archiveTarget;
    if (!formRow?.published_version_id) return;
    setBusyFormId(formRow.id);
    try {
      await api.post(`/forms/${formRow.id}/versions/${formRow.published_version_id}/archive`);
      toast.success("Version archived");
      setArchiveTarget(null);
      fetchForms();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to archive version");
    } finally {
      setBusyFormId(null);
    }
  };

  const createNewVersion = async (formRow) => {
    const sourceVersionId = formRow.published_version_id || formRow.draft_version_id;
    if (!sourceVersionId) return;
    setBusyFormId(formRow.id);
    try {
      await api.post(`/forms/${formRow.id}/versions/${sourceVersionId}/clone`, {});
      toast.success("New draft version created from the previous one");
      fetchForms();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create new version");
    } finally {
      setBusyFormId(null);
    }
  };

  // "Edit Current Version" -- same published version, PUBLISHED -> DRAFT,
  // only offered when `published_version_response_count` (from the list
  // query, services/formService.js#listForms) is 0. That count is
  // advisory only for this menu's enabled/disabled state -- the reopen
  // endpoint re-checks the exact same "zero form_submissions rows" rule
  // itself, server-side, at the moment of the request (see
  // services/formVersionService.js#reopenToDraft), so a stale row here
  // (e.g. someone started a response between page load and this click)
  // is still safely rejected, never silently allowed.
  const [reopenTarget, setReopenTarget] = useState(null);
  const confirmReopenCurrentVersion = async () => {
    const formRow = reopenTarget;
    if (!formRow?.published_version_id) return;
    setBusyFormId(formRow.id);
    try {
      await api.post(`/forms/${formRow.id}/versions/${formRow.published_version_id}/reopen`);
      toast.success("Version reopened — opening the builder now.");
      setReopenTarget(null);
      window.location.assign(`/${rolePrefix}/forms/${formRow.id}/builder/${formRow.published_version_id}`);
    } catch (err) {
      const data = err.response?.data;
      toast.error(data?.code === "FORM_VERSION_HAS_RESPONSES"
        ? "This version already has responses and can no longer be edited directly. Create a new version instead."
        : (data?.message || "Failed to reopen this version for editing"));
      setReopenTarget(null);
      fetchForms();
    } finally {
      setBusyFormId(null);
    }
  };

  return (
    <div className="forms-build-tab">

      <div className="fbt-toolbar">
        <div className="fbt-toolbar-filters">
          <InputGroup className="fbt-search">
            <InputGroup.Text aria-hidden="true"><i className="fa-solid fa-magnifying-glass"></i></InputGroup.Text>
            <Form.Control
              placeholder="Search forms..."
              aria-label="Search forms"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </InputGroup>
          <Form.Select aria-label="Filter by form type" style={{ maxWidth: 170 }} value={formTypeFilter} onChange={(e) => setFormTypeFilter(e.target.value)}>
            <option value="">All Form Types</option>
            {formTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </Form.Select>
          <Form.Select aria-label="Filter by status" style={{ maxWidth: 130 }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All Status</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </Form.Select>
          <Form.Select aria-label="Filter by project" style={{ maxWidth: 160 }} value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)}>
            <option value="">All Projects</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Form.Select>
          {(search || formTypeFilter || statusFilter || projectFilter) && (
            <Button
              className="unit-btn unit-btn-light"
              onClick={() => { setSearch(""); setFormTypeFilter(""); setStatusFilter(""); setProjectFilter(""); }}
            >
              <i className="fa-solid fa-filter-circle-xmark" aria-hidden="true"></i>
              Reset
            </Button>
          )}
        </div>
        <div className="fbt-toolbar-actions">
          <div className="fbt-view-toggle" role="group" aria-label="Change view">
            <button
              type="button"
              className={`fbt-view-btn ${viewMode === "grid" ? "active" : ""}`}
              aria-pressed={viewMode === "grid"}
              aria-label="Grid view"
              title="Grid view"
              onClick={() => selectViewMode("grid")}
            >
              <i className="fa-solid fa-grip" aria-hidden="true"></i>
            </button>
            <button
              type="button"
              className={`fbt-view-btn ${viewMode === "list" ? "active" : ""}`}
              aria-pressed={viewMode === "list"}
              aria-label="List view"
              title="List view"
              onClick={() => selectViewMode("list")}
            >
              <i className="fa-solid fa-list" aria-hidden="true"></i>
            </button>
          </div>
          <Can moduleKey="forms" actionKey="create">
            <Button className="unit-btn unit-btn-primary" onClick={openAdd}>
              <i className="fa-solid fa-plus" aria-hidden="true"></i>
              Create Form
            </Button>
          </Can>
          <Button className="fbt-manage-types-btn" variant="link" onClick={() => window.location.assign(`/${rolePrefix}/form-types`)}>
            Manage Form Types
          </Button>
        </div>
      </div>

      <div className="fbt-count-text">{total} form{total === 1 ? "" : "s"}</div>

      {loading ? (
        <div className="unit-loader"><Spinner animation="border" /><p>Loading forms...</p></div>
      ) : forms.length === 0 ? (
        <div className="unit-empty">
          <div className="unit-empty-icon"><i className="fa-solid fa-file-circle-plus" aria-hidden="true"></i></div>
          <h5>No forms found</h5>
          <p>Create your first form or adjust filters.</p>
          <Can moduleKey="forms" actionKey="create">
            <Button className="unit-btn unit-btn-primary" onClick={openAdd}>
              <i className="fa-solid fa-plus" aria-hidden="true"></i>
              Create Form
            </Button>
          </Can>
        </div>
      ) : (
        <div className={viewMode === "grid" ? "fbt-card-grid" : "fbt-list"}>
          {forms.map((row) => {
            const isPublished = !!row.published_version_id;
            const hasDraft = !!row.draft_version_id;
            const versionLabel = hasDraft ? "Draft" : isPublished ? "Published" : "—";
            const canReopenVersion = isPublished && Number(row.published_version_response_count || 0) === 0;
            const menu = (
              <Dropdown>
                <Dropdown.Toggle size="sm" className="fbt-card-menu-btn" aria-label={`More actions for ${row.name}`}>
                  <i className="fa-solid fa-ellipsis-vertical" aria-hidden="true"></i>
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => goToBuilder(row)} disabled={!hasDraft && !isPublished}>
                    <i className="fa-solid fa-pen-ruler" aria-hidden="true"></i> {hasDraft ? "Continue Building" : "Open Builder"}
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => goToBuilder(row, "preview")} disabled={!hasDraft && !isPublished}>
                    <i className="fa-solid fa-eye" aria-hidden="true"></i> Preview
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => openEdit(row)}>
                    <i className="fa-solid fa-pen" aria-hidden="true"></i> Edit Details
                  </Dropdown.Item>
                  {hasDraft && (
                    <Dropdown.Item disabled={busyFormId === row.id} onClick={() => publishDraft(row)}>
                      <i className="fa-solid fa-upload" aria-hidden="true"></i> Publish Draft
                    </Dropdown.Item>
                  )}
                  {isPublished && (
                    <Can moduleKey="form_versions" actionKey="reopen">
                      <Dropdown.Item
                        disabled={busyFormId === row.id || !canReopenVersion}
                        title={!canReopenVersion ? "This version has responses and can no longer be edited directly. Create a new version instead." : undefined}
                        onClick={() => setReopenTarget(row)}
                      >
                        <i className="fa-solid fa-pen" aria-hidden="true"></i> Edit Current Version
                      </Dropdown.Item>
                    </Can>
                  )}
                  {(isPublished || hasDraft) && (
                    <Dropdown.Item disabled={busyFormId === row.id} onClick={() => createNewVersion(row)}>
                      <i className="fa-solid fa-clone" aria-hidden="true"></i> New Version
                    </Dropdown.Item>
                  )}
                  {isPublished && (
                    <Dropdown.Item disabled={busyFormId === row.id} onClick={() => setArchiveTarget(row)}>
                      <i className="fa-solid fa-box-archive" aria-hidden="true"></i> Archive Published
                    </Dropdown.Item>
                  )}
                  <Dropdown.Item onClick={() => toggleStatus(row)}>
                    <i className="fa-solid fa-power-off" aria-hidden="true"></i> {row.is_active ? "Deactivate" : "Activate"}
                  </Dropdown.Item>
                  <Dropdown.Item className="text-danger" onClick={() => openDeleteConfirm(row)}>
                    <i className="fa-solid fa-trash" aria-hidden="true"></i> Delete
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            );

            if (viewMode === "list") {
              return (
                <div key={row.id} className="fbt-list-row">
                  <div className="fbt-list-main">
                    <strong>{row.name}</strong>
                    <span className="fbt-card-code">{row.code}</span>
                  </div>
                  <span className="fbt-pill-badge">{row.form_type_name || "—"}</span>
                  <span className={`fbt-pill-badge ${versionLabel === "Published" ? "is-success" : versionLabel === "Draft" ? "is-muted" : ""}`}>{versionLabel}</span>
                  {!row.is_active && <span className="fbt-pill-badge is-danger">Inactive</span>}
                  <span className="fbt-list-cell">{row.field_count ?? 0} fields</span>
                  <span className="fbt-list-cell">{row.assignment_count ?? 0} assignments</span>
                  <Button
                    size="sm"
                    className="unit-btn unit-btn-light"
                    onClick={() => goToBuilder(row)}
                    disabled={!hasDraft && !isPublished}
                    aria-label={`${hasDraft ? "Continue Building" : "Open Builder"} — ${row.name}`}
                    title={hasDraft ? "Continue Building" : "Open Builder"}
                  >
                    <i className="fa-solid fa-pen-ruler" aria-hidden="true"></i>
                  </Button>
                  {menu}
                </div>
              );
            }

            return (
              <div key={row.id} className="fbt-card">
                <div className="fbt-card-head">
                  <div className="fbt-card-title-group">
                    <strong>{row.name}</strong>
                    <span className="fbt-card-code">{row.code}</span>
                  </div>
                  {menu}
                </div>

                <div className="fbt-card-status-group" data-testid="fbt-card-status">
                  <span className={`fbt-pill-badge fbt-dot-badge ${versionLabel === "Published" ? "is-success" : "is-muted"}`}>
                    <i className="fbt-dot" aria-hidden="true"></i>{versionLabel}
                  </span>
                  {row.form_type_name && <span className="fbt-pill-badge">{row.form_type_name}</span>}
                  {!row.is_active && <span className="fbt-pill-badge is-danger">Inactive</span>}
                </div>

                {row.description && <p className="fbt-card-desc">{row.description}</p>}

                <div className="fbt-card-stats">
                  <div className="fbt-card-stat">
                    <span className="fbt-card-stat-label">Project</span>
                    <span className="fbt-card-stat-value">
                      {row.project_name ? `${row.project_name}${row.project_module_name ? ` / ${row.project_module_name}` : ""}` : (row.department_name || "—")}
                    </span>
                  </div>
                  <div className="fbt-card-stat">
                    <span className="fbt-card-stat-label">Version</span>
                    <span className="fbt-card-stat-value">{row.version_count > 0 ? `v${row.version_count}` : "—"}</span>
                  </div>
                  <div className="fbt-card-stat">
                    <span className="fbt-card-stat-label">Fields</span>
                    <span className="fbt-card-stat-value">{row.field_count ?? 0}</span>
                  </div>
                  <div className="fbt-card-stat">
                    <span className="fbt-card-stat-label">Assignments</span>
                    <span className="fbt-card-stat-value">{row.assignment_count ?? 0}</span>
                  </div>
                </div>

                <div className="fbt-card-footer">
                  <span className="fbt-avatar" style={{ background: avatarColorFor(row.created_by_name) }}>
                    {initialsOf(row.created_by_name)}
                  </span>
                  <span className="fbt-card-footer-name">{row.created_by_name || "Unknown"}</span>
                  {row.updated_at && (
                    <span className="fbt-card-footer-date">
                      {new Date(row.updated_at).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })},{" "}
                      {new Date(row.updated_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  )}
                </div>

                <div className="fbt-card-actions">
                  <Button size="sm" className="unit-btn unit-btn-primary fbt-primary-action" onClick={() => goToBuilder(row)} disabled={!hasDraft && !isPublished}>
                    <i className="fa-solid fa-pen-ruler" aria-hidden="true"></i> {hasDraft ? "Continue Building" : "Open Builder"}
                  </Button>
                  <Button size="sm" className="unit-btn unit-btn-light" onClick={() => goToBuilder(row, "preview")} disabled={!hasDraft && !isPublished}>
                    <i className="fa-solid fa-eye" aria-hidden="true"></i> Preview
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal show={showModal} onHide={closeModal} size="lg" centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title>{editId ? "Edit Form" : "Create Form -- Step 1: Basic Details"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-2" controlId="fbt-form-name">
              <Form.Label>Form Name *</Form.Label>
              <Form.Control value={form.name} onChange={(e) => handleChange("name", e.target.value)} />
            </Form.Group>
            {!editId && (
              <Form.Group className="mb-2" controlId="fbt-form-code">
                <Form.Label>Code (optional, auto-generated if blank)</Form.Label>
                <Form.Control value={form.code} onChange={(e) => handleChange("code", e.target.value)} />
              </Form.Group>
            )}
            <Form.Group className="mb-2" controlId="fbt-form-type">
              <Form.Label>Form Type *</Form.Label>
              <Form.Select value={form.form_type_id} onChange={(e) => handleChange("form_type_id", e.target.value)}>
                <option value="">Select a form type...</option>
                {formTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-2" controlId="fbt-form-project">
              <Form.Label>Project (optional)</Form.Label>
              <Form.Select
                value={form.project_id}
                onChange={(e) => { handleChange("project_id", e.target.value); handleChange("project_module_id", ""); fetchProjectModules(e.target.value); }}
              >
                <option value="">None</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </Form.Select>
            </Form.Group>
            {!!form.project_id && (
              <Form.Group className="mb-2" controlId="fbt-form-module">
                <Form.Label>Module (optional)</Form.Label>
                <Form.Select value={form.project_module_id} onChange={(e) => handleChange("project_module_id", e.target.value)}>
                  <option value="">None</option>
                  {projectModules.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </Form.Select>
              </Form.Group>
            )}
            <Form.Group className="mb-2" controlId="fbt-form-department">
              <Form.Label>Department (optional)</Form.Label>
              <Form.Select value={form.department_id} onChange={(e) => handleChange("department_id", e.target.value)}>
                <option value="">None</option>
                {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-2" controlId="fbt-form-visibility">
              <Form.Label>Visibility Scope</Form.Label>
              <Form.Select value={form.visibility_scope} onChange={(e) => handleChange("visibility_scope", e.target.value)}>
                {VISIBILITY_SCOPES.map((s) => <option key={s} value={s}>{s}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-2" controlId="fbt-form-description">
              <Form.Label>Description</Form.Label>
              <Form.Control as="textarea" rows={2} value={form.description} onChange={(e) => handleChange("description", e.target.value)} />
            </Form.Group>
            <Form.Check
              className="mb-2"
              label="Allow multiple submissions by default"
              checked={form.allow_multiple_submissions_default}
              onChange={(e) => handleChange("allow_multiple_submissions_default", e.target.checked)}
            />
            <Form.Check
              className="mb-2"
              label="Require review by default"
              checked={form.requires_approval_default}
              onChange={(e) => handleChange("requires_approval_default", e.target.checked)}
            />
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button className="unit-btn unit-btn-light" onClick={closeModal}>Cancel</Button>
          <Button className="unit-btn unit-btn-primary" disabled={saving} onClick={handleSave}>
            {saving ? "Saving..." : editId ? "Save Changes" : "Create & Open Builder"}
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmModal
        show={!!deleteTarget}
        onHide={() => { setDeleteTarget(null); setDeleteImpact(null); }}
        onConfirm={confirmDelete}
        busy={deleteBusy}
        title="Delete Form"
        confirmLabel="Delete"
        message={
          deleteImpactLoading ? (
            <>Delete "{deleteTarget?.name}"? Checking what will be affected&hellip;</>
          ) : deleteImpact?.hasHistoricalData ? (
            <>
              Delete "{deleteTarget?.name}"?
              <br /><br />
              This form has <strong>{deleteImpact.submissions}</strong> submitted response(s)
              {deleteImpact.satisfactionRequests > 0 ? ` and ${deleteImpact.satisfactionRequests} linked feedback request(s)` : ""}.
              It will be <strong>archived</strong>, not permanently erased: its versions, assignments, submissions, and KPI mappings
              are all preserved for audit/history, but it will disappear from this active Forms list immediately.
            </>
          ) : (
            <>
              Delete "{deleteTarget?.name}"? This cannot be undone.
              {deleteImpact && (deleteImpact.versions > 0 || deleteImpact.assignments > 0 || deleteImpact.kpiMappings > 0) ? (
                <>
                  <br /><br />
                  This will also permanently remove:
                  <ul className="mb-0 text-start">
                    {deleteImpact.versions > 0 && <li>{deleteImpact.versions} version(s), including all sections/fields</li>}
                    {deleteImpact.assignments > 0 && <li>{deleteImpact.assignments} assignment(s)</li>}
                    {deleteImpact.kpiMappings > 0 && <li>{deleteImpact.kpiMappings} KPI mapping(s)</li>}
                  </ul>
                </>
              ) : null}
            </>
          )
        }
      />
      <ConfirmModal
        show={!!archiveTarget}
        onHide={() => setArchiveTarget(null)}
        onConfirm={confirmArchivePublished}
        busy={busyFormId === archiveTarget?.id}
        title="Archive Published Version"
        message={`Archive the currently published version of "${archiveTarget?.name}"? It will stop being available to fill until a new version is published.`}
      />
      <ConfirmModal
        show={!!reopenTarget}
        onHide={() => setReopenTarget(null)}
        onConfirm={confirmReopenCurrentVersion}
        busy={busyFormId === reopenTarget?.id}
        title="Edit Current Version?"
        message={
          <>
            No responses have started for this version.
            <br /><br />
            The published version of "{reopenTarget?.name}" will be moved back to Draft so you can edit it.
            Existing pending assignments will remain linked to this version.
            <br /><br />
            Publish it again when your changes are complete.
          </>
        }
        confirmLabel="Edit Version"
        confirmVariantClass="unit-btn-primary"
      />
    </div>
  );
}
